/**
 * One entry point for reporting a problem.
 *
 * The bubble, the ticket list's "New report" button and the thread's "Add a
 * picture" all open the same panel through this context; there is no composer
 * page. That matters because the reporter has to stay on the screen that is
 * wrong — the moment they navigate away, the only way back to it is the
 * browser's share picker, which is the step reporters get stuck on.
 *
 * A "something is wrong on this page" report photographs the page silently and
 * attaches the result; a question does not. Neither opens the editor: landing
 * in a drawing tool reads as the app doing something you did not ask for. The
 * picture is simply there, and marking it up is a choice made by clicking it.
 *
 * One such picture per report, not one per opening: it is taken only when
 * nothing is attached yet, and closing a draft nobody has written in throws it
 * away again.
 *
 * The draft lives here rather than in the panel so that hiding the panel to
 * take another screenshot cannot lose what has already been written.
 */
import { ReactNode, useCallback, useEffect, useState } from "react";
import { followSupportUnread } from "../../api/support-stream";
import {
	closeSupportTicket,
	createSupportTicket,
	fetchSupportTicket,
	fetchSupportTickets,
	fetchSupportUnreadCount,
	markSupportTicketSeen,
	renameSupportTicket,
	replySupportTicket,
	SupportTicket,
	uploadSupportAttachment,
} from "../../api/support-tickets";
import { useAuth } from "../../context/AuthContext";
import SupportAnnotatorDialog from "./support-annotator-dialog";
import {
	captureOtherSurface,
	capturePage,
	isOtherSurfaceCaptureSupported,
	nextPaint,
} from "./support-capture-engines";
import SupportConversationPanel from "./support-conversation-panel";
import SupportConversationTabs from "./support-conversation-tabs";
import { type DraftPictures, useDraftPictures } from "./support-draft-pictures";
import SupportHistoryList from "./support-history-list";
import {
	deriveTitle,
	describeReportContext,
	startErrorBuffer,
} from "./support-report-context";
import SupportReportPanel from "./support-report-panel";
import { hasUnseenReply } from "./support-unread";
import { CAPTURE_UI_PROPS, SupportReportContext } from "./use-support-report";
import {
	mergeRefetchedTicket,
	useSupportTicketStream,
} from "./use-support-ticket-stream";

/**
 * Whether a conversation still belongs in the tab strip.
 *
 * The reporter's own decision, and nothing else. It used to be the operator's
 * status: they marked a conversation Closed and it left the reporter's tabs
 * that instant — answer included, read or not, a conversation ending by
 * disappearing while support had the last word. Nobody can be told they are
 * finished, and going quiet for a week is not being finished either.
 */
const isCurrent = (ticket: SupportTicket): boolean =>
	ticket.closed_by_reporter_at === null;

/**
 * The order of the tab strip: the order the conversations were started in.
 *
 * Never activity. The list arrives sorted by last activity, which is right for
 * the staff queue and wrong here, because opening a tab marks it seen — a
 * write — so merely reading a conversation shuffled it to the front and moved
 * every other tab under the pointer. Two or three tabs are remembered by
 * position, and a position that changes when you look at it is not one.
 */
const inStartOrder = (a: SupportTicket, b: SupportTicket): number => a.id - b.id;

export const SupportReportProvider = ({ children }: { children: ReactNode }) => {
	const [isOpen, setIsOpen] = useState(false);
	/** The conversation the panel is showing, once there is one. */
	const [ticket, setTicket] = useState<SupportTicket | null>(null);
	const [isAwaitingTicket, setIsAwaitingTicket] = useState(false);
	const [threads, setThreads] = useState<SupportTicket[]>([]);
	// Archived conversations are not held with the current ones: they are the
	// bulk of what a long-standing account has, they appear in no tab, and
	// they are fetched when their tab is opened rather than on every load.
	const [archived, setArchived] = useState<SupportTicket[]>([]);
	const [archivedCount, setArchivedCount] = useState(0);
	const [isBrowsingHistory, setIsBrowsingHistory] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const [reply, setReply] = useState("");
	const [isCapturing, setIsCapturing] = useState(false);
	const [message, setMessage] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const draft = useDraftPictures();
	// Separate from `draft`, which holds the NEW report being composed.
	// One shared list would carry half-composed pictures into whatever
	// conversation the reader opened next.
	const replyDraft = useDraftPictures();

	useEffect(startErrorBuffer, []);

	// The badge is the whole point of answering: a reply nobody notices is a
	// reply that did not happen — so it is streamed, not polled.
	//
	// It used to poll every 60 seconds, on the reasoning that a socket for one
	// integer was not worth its weight. That had it backwards: the bubble is
	// mounted on every page in ODI, so the poll was a request per tab per
	// minute from everybody using the application, for a number that changes a
	// handful of times a day. The stream is one idle connection on an event
	// loop, and the number is only sent when it has actually changed.
	const refreshUnread = useCallback(() => {
		fetchSupportUnreadCount({ mine: true })
			.then(setUnreadCount)
			.catch(() => undefined);
	}, []);

	// Only once there is a token to send. React runs a child's effects before
	// its parent's, and AuthProvider (root.tsx) registers the Authorization
	// header in an effect of its own — so this provider, mounted underneath it
	// in app.tsx, opened the stream first and the very first connection went
	// out unauthenticated every time. The service answered 401, which is what
	// left the badge sitting at 0 with unread replies behind it.
	const { isAuthenticated } = useAuth();
	useEffect(() => {
		if (!isAuthenticated) return;
		const controller = new AbortController();
		void followSupportUnread({
			onCount: setUnreadCount,
			signal: controller.signal,
		});
		return () => controller.abort();
	}, [isAuthenticated]);

	/**
	 * Hides the report UI, photographs the page, and attaches the result.
	 *
	 * Opens the picture for markup straight away: every capture is one the
	 * reporter asked for, so they already have something in mind to point at.
	 */
	const takePicture = useCallback(
		async (into: DraftPictures = draft) => {
			setIsCapturing(true);
			await nextPaint();
			try {
				into.openEditor(into.add((await capturePage()).blob));
			} catch (exc: unknown) {
				setError(exc instanceof Error ? exc.message : String(exc));
			} finally {
				setIsCapturing(false);
			}
		},
		[draft]
	);

	/**
	 * Another tab, another window, the whole screen. Called straight from the
	 * click — `getDisplayMedia` needs user activation, so the report UI is not
	 * hidden first; the reporter is picking a surface that is not this one.
	 */
	// `into` is what makes the same two captures serve the composer and a
	// reply: the pictures must land in the draft that is about to be sent, and
	// the two are deliberately kept apart.
	const takeOtherSurface = async (into: DraftPictures = draft) => {
		try {
			const key = into.add((await captureOtherSurface()).blob);
			into.openEditor(key);
		} catch (exc: unknown) {
			// Dismissing the browser's picker is a normal way out.
			if (exc instanceof DOMException && exc.name === "NotAllowedError") return;
			setError(exc instanceof Error ? exc.message : String(exc));
		}
	};

	const loadThreads = useCallback(
		(): Promise<SupportTicket[]> =>
			fetchSupportTickets({ archived: false, mine: true })
				.then(({ tickets }) => {
					setThreads(tickets);
					return tickets;
				})
				.catch(() => []),
		[]
	);

	// Only how many, so the archive tab can carry its number without the
	// conversations behind it.
	const loadArchivedCount = useCallback(
		() =>
			fetchSupportTickets({ archived: true, limit: 1, mine: true })
				.then(({ total }) => setArchivedCount(total))
				.catch(() => undefined),
		[]
	);

	const loadArchived = useCallback(
		() =>
			fetchSupportTickets({ archived: true, mine: true })
				.then(({ tickets, total }) => {
					setArchived(tickets);
					setArchivedCount(total);
				})
				.catch(() => undefined),
		[]
	);

	// Fetched on mount, not on the click: opening the panel has to know
	// straight away whether there is a conversation to open.
	useEffect(() => {
		void loadThreads();
		void loadArchivedCount();
	}, [loadThreads, loadArchivedCount]);

	const openThread = useCallback(
		(chosen: SupportTicket) => {
			setIsBrowsingHistory(false);
			setIsAwaitingTicket(true);
			setTicket(chosen);
			markSupportTicketSeen(chosen.id)
				.then(refreshUnread)
				.catch(() => undefined);
			fetchSupportTicket(chosen.id)
				.then(setTicket)
				.catch(() => undefined);
		},
		[refreshUnread]
	);

	/**
	 * The bubble, and the two things it can mean.
	 *
	 * Someone with a conversation open is almost always coming back to it —
	 * so it opens, rather than a blank second report beside the one they are
	 * already having. Someone with none is here to say something new, and gets
	 * the empty message box. Which of the two it is answers itself from what
	 * they have; neither hides the other, since the tab strip carries the
	 * conversations and the + carries a new one.
	 *
	 * An answer they have not read wins over the merely most recent: it is the
	 * reason the badge is lit and the reason they clicked.
	 */
	const openReport = useCallback(() => {
		setError(null);
		setIsOpen(true);
		// Decided here and now, from the list already in hand — never in
		// the continuation of the refresh below. An answer arriving a
		// moment later would move the panel under whoever had meanwhile
		// clicked + or started typing.
		void loadThreads();
		// Already reading one — reopening is not a change of subject.
		if (ticket) return;
		// Nor is an unfinished report. Closing keeps what was written, so
		// coming back has to land on it rather than on some older
		// conversation the reporter had stopped thinking about.
		if (message.trim() !== "" || draft.pictures.length > 0) return;
		const current = threads.filter(isCurrent);
		const chosen = current.find((row) => hasUnseenReply(row, false)) ?? current[0];
		if (chosen) openThread(chosen);
	}, [loadThreads, draft.pictures.length, message, ticket, threads, openThread]);

	/**
	 * One conversation, opened by id from anywhere.
	 *
	 * Router state cannot carry this: `/` is the landing page, outside this
	 * provider, and it redirects again — the second hop drops whatever the
	 * first was carrying, so the panel never heard about it. The caller holds
	 * the context instead, which no redirect can lose.
	 */
	const openConversation = useCallback(
		(ticketId: number) => {
			setError(null);
			setIsOpen(true);
			setIsBrowsingHistory(false);
			setIsAwaitingTicket(true);
			void loadThreads();
			fetchSupportTicket(ticketId)
				.then((row) => {
					setTicket(row);
					markSupportTicketSeen(row.id)
						.then(refreshUnread)
						.catch(() => undefined);
				})
				.catch(() => setIsAwaitingTicket(false));
		},
		[loadThreads, refreshUnread]
	);

	const closeReport = useCallback(() => {
		setIsOpen(false);
		// An untouched draft is not a draft. Send stays disabled until the
		// reporter writes something, so a picture with no message is nothing
		// they could have sent — and keeping it would hang a photograph of
		// some page they passed through earlier on the next report they start.
		// What they wrote is theirs and survives closing.
		if (message.trim() === "") {
			draft.clear();
			setError(null);
		}
	}, [message, draft.clear]);

	/** An empty message box: no conversation selected, nothing written. */
	const resetToCompose = () => {
		setIsBrowsingHistory(false);
		draft.clear();
		setMessage("");
		setReply("");
		setError(null);
		setTicket(null);
		setIsAwaitingTicket(false);
	};

	/** The + tab: an empty message box, nothing attached. */
	const startAnother = resetToCompose;

	/**
	 * Sending keeps the reporter exactly where they are. The panel turns into
	 * the conversation; it never hands them a ticket page.
	 */
	const send = async () => {
		setIsSending(true);
		setIsAwaitingTicket(true);
		setError(null);
		try {
			// The context rides in its own column, not appended to the message:
			// the reporter should not have to read their own user-agent string
			// back in their conversation.
			const created = await createSupportTicket(
				deriveTitle(message),
				message.trim(),
				describeReportContext(),
				draft.pictures.length
			);
			const openingMessage = created.messages[0];
			for (const picture of draft.pictures) {
				await uploadSupportAttachment(
					openingMessage.id,
					new File([picture.blob], `screenshot-${picture.key}.png`, {
						type: "image/png",
					}),
					picture.caption
				);
			}
			draft.clear();
			setMessage("");
			// Re-read so the pictures just uploaded come back on the message.
			setTicket(await fetchSupportTicket(created.id));
			refreshUnread();
			loadThreads();
		} catch (exc: unknown) {
			setError(exc instanceof Error ? exc.message : String(exc));
			setIsAwaitingTicket(false);
		} finally {
			setIsSending(false);
		}
	};

	/** The reporter naming their own conversation, so its tab reads as one word. */
	const rename = async (chosen: SupportTicket, label: string) => {
		const renamed = await renameSupportTicket(chosen.id, label);
		setTicket((current) =>
			current && current.id === chosen.id
				? { ...current, label: renamed.label }
				: current
		);
		loadThreads();
	};

	/** Closing is a message and a status, so the operator sees a reason. */
	const closeThread = async (chosen: SupportTicket) => {
		await closeSupportTicket(chosen.id);
		// Both lists move: the conversation leaves the strip AND joins the
		// archive. Only the strip was refreshed, so the archive tab — which
		// renders on `olderCount > 0` — stayed hidden until the next reload,
		// and a reporter who closed their only conversation watched it
		// disappear with nowhere to find it again.
		const [rows] = await Promise.all([loadThreads(), loadArchivedCount()]);
		if (chosen.id !== ticket?.id) return;
		// Finishing one conversation is not the start of another. It used to
		// go through the + tab, which photographs the page — so the panel
		// vanished for as long as the capture took and came back holding a
		// picture of a page nobody had complained about. Whatever is still
		// open takes over instead; if nothing is, the empty message box does.
		const remaining = rows.filter(isCurrent);
		if (remaining.length > 0) {
			openThread(remaining[0]);
			return;
		}
		resetToCompose();
	};

	const sendReply = async () => {
		if (!ticket) return;
		setIsSending(true);
		try {
			// Pictures hang off the message they were sent with, so the reply
			// has to exist before they can be uploaded to it.
			const message = await replySupportTicket(
				ticket.id,
				reply.trim(),
				replyDraft.pictures.length
			);
			for (const picture of replyDraft.pictures) {
				await uploadSupportAttachment(
					message.id,
					new File([picture.blob], `screenshot-${picture.key}.png`, {
						type: "image/png",
					}),
					picture.caption
				);
			}
			replyDraft.clear();
			setReply("");
			setTicket(await fetchSupportTicket(ticket.id));
			refreshUnread();
		} catch (exc: unknown) {
			setError(exc instanceof Error ? exc.message : String(exc));
		} finally {
			setIsSending(false);
		}
	};

	// An older conversation opened from the history keeps a tab while it is the
	// one being read, so the strip never selects something it does not show.
	// `threads` holds only what is open — the server filters it — so the guard
	// stays: closing one puts it in `archived` on the next fetch, and it must
	// leave the strip the moment it is closed rather than one fetch later.
	const current = threads.filter(isCurrent).sort(inStartOrder);
	const tabTickets =
		ticket && !current.some((candidate) => candidate.id === ticket.id)
			? [...current, ticket]
			: current;

	// The panel stays put while the shutter is open. It used to be unmounted
	// for the duration, which is what made adding a picture flash: the whole
	// widget vanished, the page reflowed without it, and it came back — for as
	// long as snapDOM took, on the one gesture where the reporter is looking
	// straight at it. Nothing needed it: every node the report UI owns carries
	// CAPTURE_UI_PROPS and snapDOM is told to drop that selector from the
	// clone, so the picture never contains the panel whether or not it is on
	// screen. What the reporter sees instead is the spinner in the picture
	// strip, where the new picture is about to appear.
	const isPanelVisible = isOpen && draft.editing === null;

	// The stream is opened by the conversation being ON SCREEN, not by this
	// provider being mounted — it is mounted on every page in ODI, and a
	// connection per open tab is exactly what the separate stream service was
	// built to avoid paying for. Reading a conversation is the rare state; the
	// bubble sitting in the corner is the common one.
	//
	// Not while browsing the archive either: that view shows a list, and the
	// conversation behind it is not being read.
	const openConversationId =
		isPanelVisible && !isBrowsingHistory ? (ticket?.id ?? null) : null;
	useSupportTicketStream({
		ticketId: openConversationId,
		afterMessageId: ticket?.messages.at(-1)?.id ?? 0,
		isActive: openConversationId !== null,
		onMessage: (message) => {
			// REPLACE by id, never skip. A message arrives twice by design:
			// once when it is written, and again when a picture lands on it —
			// an attachment needs a message id to hang on, so it cannot travel
			// with the reply that carries it. Treating the second as a
			// duplicate is what left the words on screen and the picture
			// visible only after a reload.
			const merge = (held: SupportTicket): SupportTicket =>
				held.id !== message.ticket
					? held
					: {
							...held,
							messages: [
								...held.messages.filter(
									(existing) => existing.id !== message.id
								),
								message,
							].sort((a, b) => a.id - b.id),
						};
			setTicket((current) => (current ? merge(current) : current));
			// The tab strip reads from `threads`, so a conversation the
			// reporter switches away from and back to must already have it.
			setThreads((current) => current.map(merge));
			// Read as it arrives. A staff answer landing in the conversation
			// on screen is seen, so it does not light the badge.
			if (!message.is_from_reporter) {
				markSupportTicketSeen(message.ticket)
					.then(refreshUnread)
					.catch(() => undefined);
			}
		},
		onLabel: (label) => {
			// Named a moment after it was created: the tab turns from its
			// number into the label while the reporter is looking at it.
			const named = (held: SupportTicket): SupportTicket =>
				held.id === openConversationId ? { ...held, label } : held;
			setTicket((current) => (current ? named(current) : current));
			setThreads((current) => current.map(named));
		},
		onTicket: (row) => {
			setTicket((current) =>
				current ? mergeRefetchedTicket(current, row) : current
			);
			setThreads((current) =>
				current.map((held) => mergeRefetchedTicket(held, row))
			);
		},
	});

	return (
		<SupportReportContext.Provider
			value={{
				openReport,
				openConversation,
				closeReport,
				isOpen,
				unreadCount,
			}}
		>
			{children}
			{isPanelVisible && (
				<div {...CAPTURE_UI_PROPS}>
					<SupportReportPanel
						tabs={
							<SupportConversationTabs
								tickets={tabTickets}
								activeId={
									isAwaitingTicket ? (ticket?.id ?? null) : null
								}
								hasUnseen={(candidate) =>
									hasUnseenReply(candidate, false)
								}
								onSelect={openThread}
								onStartNew={startAnother}
								onRename={(chosen, label) => void rename(chosen, label)}
								olderCount={archivedCount}
								isBrowsingHistory={isBrowsingHistory}
								onShowHistory={() => {
									setIsBrowsingHistory(true);
									setIsAwaitingTicket(false);
									// Fetched on the click, not on every load.
									void loadArchived();
								}}
							/>
						}
						conversation={
							isBrowsingHistory ? (
								<SupportHistoryList
									tickets={archived}
									onOpen={openThread}
								/>
							) : isAwaitingTicket ? (
								<SupportConversationPanel
									ticket={ticket}
									reply={reply}
									isSending={isSending}
									onReplyChange={setReply}
									onSendReply={() => void sendReply()}
									onClose={(chosen) => void closeThread(chosen)}
									pictures={replyDraft.pictures}
									onAddPicture={(blob) =>
										replyDraft.openEditor(replyDraft.add(blob))
									}
									onEditPicture={replyDraft.openEditor}
									onRemovePicture={replyDraft.remove}
									pictureSources={{
										isCapturing,
										// Into the REPLY's draft, not the
										// composer's — same capture, different
										// destination.
										onAddPicture: () =>
											void takePicture(replyDraft),
										onAddOtherSurface:
											isOtherSurfaceCaptureSupported()
												? () =>
														void takeOtherSurface(
															replyDraft
														)
												: null,
									}}
								/>
							) : null
						}
						message={message}
						pictures={draft.pictures}
						isSending={isSending}
						isCapturing={isCapturing}
						error={error}
						onMessageChange={setMessage}
						onEditPicture={draft.openEditor}
						onRemovePicture={draft.remove}
						onAddPicture={() => void takePicture()}
						onAddOtherSurface={
							isOtherSurfaceCaptureSupported()
								? () => void takeOtherSurface()
								: null
						}
						onUploadPicture={(file) => draft.openEditor(draft.add(file))}
						onSend={() => void send()}
						onClose={closeReport}
					/>
				</div>
			)}
			<SupportAnnotatorDialog
				image={draft.editing?.blob ?? null}
				initialCaption={draft.editing?.caption ?? ""}
				onAttach={draft.replaceEdited}
				onCancel={draft.closeEditor}
			/>
			{/* Its counterpart for a reply's pictures. A second instance rather
			    than one dialog over a merged draft: only one of the two can be
			    editing at a time, and keeping the drafts apart is what stops a
			    half-composed report's pictures following the reader into a
			    conversation. Marking up matters just as much on a reply — the
			    answer to "which page?" is usually a picture with a ring on it. */}
			<SupportAnnotatorDialog
				image={replyDraft.editing?.blob ?? null}
				initialCaption={replyDraft.editing?.caption ?? ""}
				onAttach={replyDraft.replaceEdited}
				onCancel={replyDraft.closeEditor}
			/>
		</SupportReportContext.Provider>
	);
};
